import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { newId, photoPath, queuePhoto, useCapturePhoto, useOutbox } from '@dia/db';
import { useT } from '@dia/i18n';
import { Button, PhotoCapture, cn } from '@dia/ui';
import { sync } from '../supabase';
import { RequireAuth, type Identity } from './lib/identity';
import { Icon } from './lib/icons';
import { loadAssets, loadJobCatalog, useLoad } from './lib/data';
import { sendMaintenanceRequest } from './lib/requests';
import { extensionForMime, MAX_VOICE_SECONDS, useVoiceNote } from './lib/voice';
import { Card, Field, inputClass, Notice, Page } from './lib/ui';

/**
 * Mantenimiento: report something broken in three taps. Pick the asset or the
 * room, take a photo, optionally record a 20 second voice note, send. The
 * request lands in the queue with a Spanish and an English text.
 */
export function Mantenimiento() {
  return <RequireAuth>{(who) => <ReportBody who={who} />}</RequireAuth>;
}

/** The room select's own value for "it's not in this list, let me type it". */
const OTHER_PLACE = '__other__';

function ReportBody({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const [params] = useSearchParams();
  const { enqueue, online } = useOutbox();
  const assets = useLoad(loadAssets, 'assets');
  const catalog = useLoad(loadJobCatalog, 'catalog');

  const presetAsset = params.get('asset');
  const [requestId, setRequestId] = useState(newId);
  const [mode, setMode] = useState<'asset' | 'room'>('asset');
  const [assetId, setAssetId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [otherPlace, setOtherPlace] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [missing, setMissing] = useState(false);
  const voice = useVoiceNote();

  // A link from a Sistemas page arrives with the asset already chosen.
  const presetId = useMemo(
    () => (presetAsset ? (assets.data?.find((a) => a.slug === presetAsset)?.id ?? '') : ''),
    [presetAsset, assets.data],
  );
  const chosenAssetId = assetId || presetId;

  const parts = useMemo(() => ({ requestId }), [requestId]);
  const capture = useCapturePhoto({ bucket: 'request-photos', parts });

  const houseName = (id: string) => {
    const h = catalog.data?.houses.find((x) => x.id === id);
    return h ? (lang === 'es' ? h.name_es : h.name_en) : '';
  };

  const submit = async () => {
    const asset = assets.data?.find((a) => a.id === (mode === 'asset' ? chosenAssetId : ''));
    const room = catalog.data?.rooms.find((r) => r.id === (mode === 'room' ? roomId : ''));
    const isOtherPlace = mode === 'room' && roomId === OTHER_PLACE;
    const otherPlaceText = otherPlace.trim();
    if (!asset && !room && !(isOtherPlace && otherPlaceText)) {
      setMissing(true);
      return;
    }
    setMissing(false);
    setBusy(true);
    try {
      let voicePath: string | null = null;
      if (voice.blob) {
        voicePath = photoPath({ requestId, itemId: 'voz', ext: extensionForMime(voice.blob.type) });
        await queuePhoto({ id: newId(), bucket: 'request-photos', path: voicePath, blob: voice.blob });
        void sync.tick('enqueue');
      }
      await sendMaintenanceRequest({
        enqueue,
        requestId,
        staffId: who.staffId,
        token: who.token,
        lang,
        target: {
          assetEs: asset?.name_es ?? null,
          assetEn: asset?.name_en ?? null,
          // A typed location has no English side yet: the crew UI is Spanish
          // only, so it goes in as-is on both sides rather than blocking the
          // report on a translation that has nowhere to come from here (the
          // free note field is the one place that already has that pipeline).
          roomEs: room?.name_es ?? (isOtherPlace ? otherPlaceText : null),
          roomEn: room?.name_en ?? (isOtherPlace ? otherPlaceText : null),
        },
        note,
        assetId: asset?.id ?? null,
        roomId: room?.id ?? null,
        photoPaths: photos,
        voiceNotePath: voicePath,
        priority: urgent ? 1 : 3,
      });
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const again = () => {
    setRequestId(newId());
    setAssetId('');
    setRoomId('');
    setOtherPlace('');
    setPhotos([]);
    setNote('');
    setUrgent(false);
    setSent(false);
    voice.discard();
  };

  if (sent) {
    return (
      <Page title={t('ops.nav.maintenance')}>
        <div className="mt-2 flex flex-col gap-4">
          <Notice tone="ok">{t('ops.maintenance.sent')}</Notice>
          {!online && <Notice tone="warn">{t('ops.maintenance.queued')}</Notice>}
          <Button size="lg" block onClick={again}>
            {t('ops.maintenance.another')}
          </Button>
        </div>
      </Page>
    );
  }

  const rooms = catalog.data?.rooms ?? [];

  return (
    <Page title={t('ops.maintenance.title')}>
      <div className="flex flex-col gap-5">
        <Card>
          <div className="mb-3 grid grid-cols-2 gap-2" role="group">
            {(['asset', 'room'] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={cn(
                  'min-h-tap rounded border px-2 py-2 text-[18px]',
                  mode === m ? 'border-accent bg-accent font-semibold text-onAccent' : 'border-line bg-surface text-ink',
                )}
              >
                {m === 'asset' ? t('ops.maintenance.what_tab') : t('ops.maintenance.where_tab')}
              </button>
            ))}
          </div>

          {mode === 'asset' ? (
            <Field label={t('ops.maintenance.pick_asset')}>
              <select value={chosenAssetId} onChange={(e) => setAssetId(e.target.value)} className={inputClass}>
                <option value="">{t('ops.maintenance.pick_none')}</option>
                {(assets.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {lang === 'es' ? a.name_es : a.name_en}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label={t('ops.maintenance.pick_place')}>
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className={inputClass}>
                <option value="">{t('ops.maintenance.pick_none')}</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {houseName(r.house_id)}: {lang === 'es' ? r.name_es : r.name_en}
                  </option>
                ))}
                <option value={OTHER_PLACE}>{t('ops.maintenance.pick_other')}</option>
              </select>
              {roomId === OTHER_PLACE && (
                <input
                  type="text"
                  value={otherPlace}
                  onChange={(e) => setOtherPlace(e.target.value)}
                  placeholder={t('ops.maintenance.other_place_label')}
                  aria-label={t('ops.maintenance.other_place_label')}
                  className={cn(inputClass, 'mt-2')}
                />
              )}
            </Field>
          )}
          {missing && <div className="mt-3"><Notice tone="danger">{t('ops.maintenance.need_target')}</Notice></div>}
        </Card>

        <Card>
          <h2 className="mb-2 flex items-center gap-2 text-[1.15em] font-semibold">
            <Icon name="camera" /> {t('ops.maintenance.photo_title')}
          </h2>
          <PhotoCapture
            max={3}
            onCapture={(p) =>
              void capture(p.file).then((r) => setPhotos((prev) => [...prev, r.path]))
            }
          />
        </Card>

        <Card>
          <h2 className="mb-2 flex items-center gap-2 text-[1.15em] font-semibold">
            <Icon name="mic" /> {t('ops.maintenance.voice_note')}
          </h2>
          {voice.state === 'unsupported' && <Notice tone="info">{t('ops.maintenance.voice_unsupported')}</Notice>}
          {voice.state === 'denied' && <Notice tone="warn">{t('ops.maintenance.voice_denied')}</Notice>}
          {(voice.state === 'idle' || voice.state === 'denied') && (
            <Button variant="secondary" block onClick={() => void voice.start()}>
              <Icon name="mic" /> {t('ops.maintenance.voice_note')}
            </Button>
          )}
          {voice.state === 'recording' && (
            <div className="flex flex-col gap-2">
              <Notice tone="danger">{t('ops.maintenance.voice_recording', { seconds: Math.min(voice.seconds, MAX_VOICE_SECONDS) })}</Notice>
              <Button variant="danger" block onClick={voice.stop}>
                {t('ops.maintenance.voice_stop')}
              </Button>
            </div>
          )}
          {voice.state === 'ready' && (
            <div className="flex flex-col gap-2">
              <Notice tone="ok">{t('ops.maintenance.voice_saved')}</Notice>
              <Button variant="secondary" block onClick={voice.discard}>
                {t('ops.maintenance.voice_remove')}
              </Button>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex flex-col gap-4">
            <Field label={t('ops.maintenance.note_label')}>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={inputClass} />
            </Field>
            <label className="flex min-h-tap items-center gap-3">
              <input
                type="checkbox"
                checked={urgent}
                onChange={(e) => setUrgent(e.target.checked)}
                className="h-6 w-6 accent-[rgb(var(--c-accent))]"
              />
              <span className="font-medium">{t('ops.maintenance.urgent')}</span>
            </label>
          </div>
        </Card>

        <Button size="lg" block busy={busy} busyLabel={t('loading')} onClick={() => void submit()}>
          <Icon name="send" /> {t('ops.maintenance.send')}
        </Button>
      </div>
    </Page>
  );
}
